import { Dispatch } from 'react';
import {
  BgImageItem,
  BgImagePaginateList,
  CategoryItem,
  ColumnPaginateList,
  IBgImageDetail,
  IBgImageRequestParams,
  ICoverpageDetail,
  LibraryEditItem,
  LibraryItem,
  LibraryListParam,
  PromotionImageDetail,
  ITypeOfView,
  LibraryPaginateList,
  CategoryPaginateList,
} from '../types';
import { filterQueryForPublish } from '../utils/common-utils';
import { getDayEnd, getDayStartForStrapi, ignoreTimezone } from '@/app/common/utils/time-utils';
import moment from 'moment';
import qs from 'qs';
import { apiClient, apiErrorHandler } from 'src/app/common/network';
import { CreateBlobParams, GetBlobParams } from 'src/app/common/network';
import { ResourceCreateBlobRes, ResourceGetBlobRes } from '@/app/common/resource/resource-types';
import { defaultTextColorOptions } from '../pages/SalesMaterial/constants';

const AGENT_URL = window.envConfig['REACT_APP_APIM_BASE_URL'];
const APIM_AGENT = window.envConfig['REACT_APP_APIM_AGENT'];

export const fetchLibraryList = async (
  locale: string,
  params: LibraryListParam,
  sortKeys: { key: string; value?: string }[],
  dispatch?: Dispatch<any>,
  signal?: AbortSignal,
): Promise<LibraryPaginateList> => {
  try {
    const resolveDateParam = (value: unknown): Date | null => {
      if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
      }

      if (typeof value === 'string' && value.trim()) {
        const parsedDate = new Date(value);
        return isNaN(parsedDate.getTime()) ? null : parsedDate;
      }

      return null;
    };

    const { module, customizeQueryBuilder } = params;
    const baseUrl = `${AGENT_URL}${APIM_AGENT}/content-manager/collection-types/api::material.material?`;

    // ✅ Xây dựng query object chuẩn thay vì nối chuỗi thủ công
    const query: Record<string, any> = {
      filters: {},
      sort: [],
    };

    // ✅ Default filters - có thể override từ params nếu cần
    query.filters.eventInvitation = { $eq: true };
    query.filters.eventForAd = { $eq: true };
    query.filters.module = { $eq: module };

    // ✅ Xử lý status publish
    const statusFilter = filterQueryForPublish(params.status, '');
    if (statusFilter) {
      Object.assign(query.filters, qs.parse(statusFilter).filters || {});
    }

    const handledParams = new Set([
      'module',
      'roleAdActing',
      'officeCodes',
      'status',
      'isPublish',
      'isValid',
      'lastUpdatedBySub',
      'customizeQueryBuilder',
    ]);

    // ✅ Xử lý tất cả parameters một cách hệ thống
    Object.entries(params).forEach(([key, param]) => {
      if (!param || handledParams.has(key)) {
        return;
      }

      // Pagination params
      if (['page', 'pageSize'].includes(key)) {
        query[key] = param;
        return;
      }

      // Search name with escape special characters
      if (key === 'name') {
        const escapedParam = (param as string).replace(/([!@#$%^&*()+=\[\]\\';,./{}|":<>?~_-])/g, '\\$1');
        query.filters.name = { $containsi: escapedParam };
        return;
      }

      // Date filters
      const parsedDate = resolveDateParam(param);
      if (parsedDate) {
        const keyLower = key.toLowerCase();
        if (keyLower.includes('createtimestart')) {
          query.filters.createdAt = { ...query.filters.createdAt, $gte: getDayStartForStrapi(parsedDate) };
        } else if (keyLower.includes('createtimeend')) {
          query.filters.createdAt = { ...query.filters.createdAt, $lte: ignoreTimezone(getDayEnd(parsedDate)) };
        } else if (keyLower.includes('eventstartdate')) {
          query.filters.eventStartDate = {
            ...query.filters.eventStartDate,
            $gte: getDayStartForStrapi(parsedDate),
          };
        } else if (keyLower.includes('eventenddate')) {
          query.filters.eventStartDate = {
            ...query.filters.eventStartDate,
            $lte: ignoreTimezone(getDayEnd(parsedDate)),
          };
        } else if (keyLower.includes('effectivedate')) {
          const curTime = moment(parsedDate).format('YYYY-MM-DDTHH:mm:ss.SSS');
          query.filters.status = 'Published';
          query.filters.$or = [
            { effectiveDate: { $lte: curTime }, expiredDate: { $gte: curTime } },
            { effectiveDate: { $lte: curTime }, expiredDate: { $null: true } },
            { effectiveDate: { $null: true }, expiredDate: { $gte: curTime } },
            { effectiveDate: { $null: true }, expiredDate: { $null: true } },
          ];
        }
        return;
      }

      // Category filter
      if (key === 'category' && typeof param === 'string') {
        query.filters.category = { name: { $eq: param } };
        return;
      }

      // Column filter
      if (key === 'column') {
        query.populate = {
          columnReferences: { populate: '*' },
        };
        query.filters.columnReferences = {
          column: { id: param },
        };
        return;
      }

      // Type equals filter
      if (key === 'type_eq') {
        query.filters.type = { $eq: param };
        return;
      }

      // Default containsi filter cho string
      if (typeof param === 'string') {
        query.filters[key] = { $containsi: param };
        return;
      }

      // Giá trị thông thường
      query[key] = param;
    });

    // ✅ Role AD + Office filter
    if (params.roleAdActing) {
      const account = params.lastUpdatedBySub;
      const officeCodes = Array.isArray(params.officeCodes) ? params.officeCodes.filter(Boolean) : [];
      const roleOrFilters: Record<string, any>[] = [];

      switch (params.roleAdActing) {
        case 'BDM': {
          query.filters.$or = [
            { createdByRole: { $eq: 'BDM' } },
            { createdByRole: { $null: true } },
            { createdByRole: { $exists: false } },
          ];
          if (account) {
            query.filters.lastUpdatedBySub = { $eq: account };
          }
          break;
        }

        case 'BDD': {
          if (account) {
            roleOrFilters.push({
              $and: [{ createdByRole: { $eq: 'BDD' } }, { lastUpdatedBySub: { $eq: account } }],
            });
          }

          officeCodes.forEach((office) => {
            roleOrFilters.push({
              $and: [
                { eventForOffices: { $containsi: office } },
                {
                  $or: [
                    { createdByRole: { $eq: 'BDM' } },
                    { createdByRole: { $null: true } },
                    { createdByRole: { $exists: false } },
                  ],
                },
              ],
            });
          });
          break;
        }

        case 'TH': {
          if (account) {
            roleOrFilters.push({
              $and: [{ createdByRole: { $eq: 'TH' } }, { lastUpdatedBySub: { $eq: account } }],
            });
          }

          officeCodes.forEach((office) => {
            roleOrFilters.push({
              $and: [
                { eventForOffices: { $containsi: office } },
                {
                  $or: [
                    { createdByRole: { $eq: 'BDD' } },
                    { createdByRole: { $eq: 'BDM' } },
                    { createdByRole: { $null: true } },
                    { createdByRole: { $exists: false } },
                  ],
                },
              ],
            });
          });
          break;
        }

        default:
          break;
      }

      if (roleOrFilters.length > 0) {
        if (query.filters.$or) {
          query.filters.$and = query.filters.$and || [];
          query.filters.$and.push({ $or: query.filters.$or });
          query.filters.$and.push({ $or: roleOrFilters });
          delete query.filters.$or;
        } else {
          query.filters.$or = roleOrFilters;
        }
      }
    }

    // ✅ Xử lý sorting
    sortKeys.forEach((sortParam) => {
      if (sortParam.value) {
        query.sort.push(`${sortParam.key}:${sortParam.value}`);
      }
    });
    if (query.sort.length === 0) {
      query.sort.push('updatedAt:DESC');
    }

    // ✅ Locale
    if (locale) {
      query.locale = locale;
    }

    // ✅ Allow callers to customize final query payload before request
    if (typeof customizeQueryBuilder === 'function') {
      const customizedQuery = customizeQueryBuilder();
      if (customizedQuery && typeof customizedQuery === 'object') {
        query.filters = {
          ...query.filters,
          ...customizedQuery,
        };
      }
    }

    // ✅ Gọi API chính với signal support để cancel request
    const listResponse = await apiClient.get(baseUrl, {
      params: query,
      paramsSerializer: (p) => qs.stringify(p, { encodeValuesOnly: true }),
      signal,
    });

    const { results, pagination } = listResponse.data;

    // ✅ Chuẩn hóa response format chuẩn
    const result: LibraryPaginateList = {
      libraryList: results,
      items: results,
      docs: results,
      total: pagination.total,
      totalPages: pagination.pageCount,
      limit: pagination.pageSize,
      totalRecords: pagination.total,
      totalDocs: pagination.total,
      hasNextPage: pagination.page < pagination.pageCount,
      hasPrevPage: pagination.page > 1,
    };

    // ✅ Nếu không có data thì return ngay - không gọi API thừa
    if (results.length === 0) {
      return result;
    }

    // ✅ Lấy column references cho các item
    const uuids = results.map((item: { uuid: string }) => item.uuid).filter(Boolean);

    if (uuids.length > 0) {
      const columnResponse = await apiClient.get(`${AGENT_URL}${APIM_AGENT}/content-manager/api/materials`, {
        params: {
          fields: ['uuid'],
          populate: { columnReferences: { populate: '*' } },
          filters: { uuid: uuids },
          publicationState: 'preview',
          pagination: { pageSize: uuids.length },
        },
        paramsSerializer: (p) => qs.stringify(p, { encodeValuesOnly: true }),
        headers: { 'raw-path': true },
        signal,
      });

      // ✅ Tạo Map O(1) thay vì find O(n) bên trong loop -> tối ưu performance lớn
      const columnReferenceMap = new Map<string, any[]>();

      columnResponse.data.data.forEach((item: any) => {
        const formattedRefs =
          item.attributes.columnReferences?.map((ref: any) => ({
            id: ref.id,
            weight: ref.weight,
            module: ref.module,
            column: ref.column.data ? { ...ref.column.data.attributes, id: ref.column.data.id } : null,
          })) || [];

        columnReferenceMap.set(item.attributes.uuid, formattedRefs);
      });

      // ✅ Gán column reference cho từng item với O(1) lookup
      results.forEach((libraryItem: any) => {
        libraryItem.columnReferences = columnReferenceMap.get(libraryItem.uuid) || [];
      });
    }

    return result;
  } catch (error: any) {
    // ✅ Ignore abort error - không cần throw khi user cancel request
    if (error.name === 'AbortError') {
      return {
        libraryList: [],
        items: [],
        docs: [],
        total: 0,
        totalPages: 0,
        limit: 0,
        totalRecords: 0,
        totalDocs: 0,
        hasNextPage: false,
        hasPrevPage: false,
      };
    }

    console.error('[fetchLibraryList] Error:', error);
    throw apiErrorHandler(error, dispatch);
  }
};

export const fetchAllColumnList = async (moduleType: string, dispatch?: Dispatch<any>): Promise<ColumnPaginateList> => {
  let queryPath = `${AGENT_URL}${APIM_AGENT}/content-manager/collection-types/api::column.column?`;

  // Query for corresponding module
  // queryPath += `module_eq=${moduleType}`;
  queryPath += `filters[module][$eq]=${moduleType}&page=1&pageSize=1000`;

  return apiClient
    .get(queryPath)
    .then((response: any) => {
      return {
        columnList: response.data.results,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.pageCount,
        limit: response.data.pagination.pageSize,
        items: response.data.results,
        totalRecords: response.data.pagination.total,
        docs: response.data.results,
        hasNextPage: true,
        hasPrevPage: false,
        totalDocs: response.data.pagination.total,
      };
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const modifyLibrary = async (
  code: string,
  data: LibraryEditItem,
  dispatch?: Dispatch<any>,
): Promise<LibraryItem> => {
  const modifyURL = `${AGENT_URL}${APIM_AGENT}/content-manager/collection-types/api::material.material/${code}`;
  return apiClient
    .put<LibraryItem>(modifyURL, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      return response.data;
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const editUnpublishLibrary = async (
  dataId: string,
  data: LibraryEditItem,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  const publishURL = `${AGENT_URL}${APIM_AGENT}/content-manager/collection-types/api::material.material/${dataId}/actions/unpublish`;
  return apiClient
    .post<void>(publishURL, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      return response.data;
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchLibraryItem = (id: string, dispatch?: Dispatch<any>): Promise<LibraryItem> => {
  const queryPath = `${AGENT_URL}${APIM_AGENT}/content-manager/api/materials/${id}?${qs.stringify({
    populate: {
      columnReferences: {
        populate: '*',
      },
      childItems: {
        populate: '*',
      },
      category: {
        populate: '*',
      },
      localizations: {
        populate: '*',
      },
      typeOfViews: {
        populate: '*',
      },
    },
  })}`;

  return apiClient
    .get(queryPath, {
      headers: {
        'raw-path': true,
      },
    })
    .then((response: any) => {
      const item = response.data.data.attributes;

      if (item.categorySnapshot) {
        item.category = {
          value: item.categorySnapshot.id,
          label: item.categorySnapshot.name,
        };
      } else {
        if (item.category.data) {
          item.category = {
            value: item.category.data.id,
            label: item.category.data.attributes.name,
          };
        }
      }

      delete item.categorySnapshot;

      item.typeOfViews =
        item?.typeOfViews?.data?.map((typeOfView: any) => {
          return { ...typeOfView?.attributes, id: typeOfView.id };
        }) || [];

      item.columnReferences = item.columnReferences.map(
        (i: { column: { data: { id: string; attributes: any } }; module: string; weight: number; id: string }) => {
          i.column.data.attributes.id = i.column.data.id;
          i.column = i.column.data.attributes;
          return i;
        },
      );
      item.childItems = item.childItems.data.map((material: any) => {
        material.attributes.id = material.id;
        material.attributes.localizations = (material.attributes.localizations?.data || []).map((lo: any) => {
          lo.attributes.id = lo.id;
          return lo.attributes;
        });
        return material.attributes;
      });
      item.localizations = item.localizations.data.map((i: { id: string; attributes: any }) => {
        i.attributes.id = i.id;
        return i.attributes;
      });
      return item;
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const publishLibrary = async (dataId: string, dispatch?: Dispatch<any>): Promise<void> => {
  const publishURL = `${AGENT_URL}${APIM_AGENT}/content-manager/collection-types/api::material.material/${dataId}/actions/publish`;
  return apiClient
    .post<void>(
      publishURL,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    .then((response: any) => {
      return response.data;
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const unpublishLibrary = async (dataId: string, dispatch?: Dispatch<any>): Promise<void> => {
  const unpublishURL = `${AGENT_URL}${APIM_AGENT}/content-manager/collection-types/api::material.material/${dataId}/actions/unpublish`;
  return apiClient
    .post<void>(
      unpublishURL,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    .then((response: any) => {
      return response.data;
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const createNewLibrary = async (
  data: LibraryEditItem,
  locale: string,
  relatedId?: string,
  dispatch?: Dispatch<any>,
): Promise<LibraryItem> => {
  let createURL = `${AGENT_URL}${APIM_AGENT}/content-manager/collection-types/api::material.material?locale=${locale}`;
  if (relatedId) {
    createURL += `&relatedEntityId=${relatedId}`;
  }

  return apiClient
    .post<LibraryItem>(createURL, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      return response.data;
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const deleteLibrary = async (dataId: string, dispatch?: Dispatch<any>): Promise<void> => {
  const deleteURL = `${AGENT_URL}${APIM_AGENT}/content-manager/collection-types/api::material.material/${dataId}`;
  return apiClient
    .delete<void>(deleteURL, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      return response.data;
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchAllColumns = async (module: string) => {
  const query = {
    publicationState: 'preview',
    pagination: {
      limit: -1,
    },
    filters: {
      module,
      publishedAt: {
        $notNull: true,
      },
    },
  };

  const queryPath = `${AGENT_URL}${APIM_AGENT}/content-manager/api/columns?${qs.stringify(query)}`;

  return apiClient
    .get(queryPath, {
      headers: {
        'raw-path': true,
      },
    })
    .then((response: any) => {
      const data = response.data;
      data.data = (data.data || []).map((item: any) => {
        item.attributes.id = item.id;
        item.attributes._id = item.id;
        return item.attributes;
      });

      return data.data;
    });
};

export const getBgImageById = async (id: string): Promise<IBgImageDetail> => {
  const queryPath = `${AGENT_URL}${APIM_AGENT}/ad/background-images/${id}`;

  return apiClient
    .get(queryPath, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      return response.data;
    });
};

export const getPromotionImageById = async (id: string): Promise<PromotionImageDetail> => {
  const queryPath = `${AGENT_URL}${APIM_AGENT}/ad/promotion-images/${id}`;

  return apiClient
    .get(queryPath, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      return response.data;
    });
};

export const fetchAllBgImageRequest = async ({
  name = '',
  page = 1,
  limit = 20,
  type,
  templateId,
}: IBgImageRequestParams): Promise<BgImagePaginateList> => {
  const query = {
    name,
    page,
    limit,
    type,
    templateId,
  };

  const queryPath = `${AGENT_URL}${APIM_AGENT}/ad/background-images?${qs.stringify(query)}`;

  return apiClient
    .get(queryPath, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      if (response?.data?.items?.length > 0) {
        response.data.items = convertToBgImageOption(response.data.items, (page - 1) * limit);
      }
      return response.data ? { ...response.data, page, limit } : {};
    });
};

export const createBlob = async (
  params: CreateBlobParams,
  dispatch?: Dispatch<any>,
): Promise<ResourceCreateBlobRes> => {
  return apiClient
    .post<ResourceCreateBlobRes>(`${AGENT_URL}${APIM_AGENT}/ad/resource`, params, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => response.data)
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const getBlob = async (params: GetBlobParams, dispatch?: Dispatch<any>): Promise<ResourceGetBlobRes[]> => {
  return apiClient
    .get<ResourceGetBlobRes[]>(`${AGENT_URL}${APIM_AGENT}/ad/resource`, { params })
    .then((response: any) => response.data)
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const createNewTypeOfView = async (data: ITypeOfView, dispatch?: Dispatch<any>): Promise<ITypeOfView> => {
  const createURL = `${AGENT_URL}${APIM_AGENT}/ad/material/handle-type-of-view`;
  return apiClient
    .post<ITypeOfView>(createURL, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      return response.data;
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const modifyTypeOfView = async (data: ITypeOfView, dispatch?: Dispatch<any>): Promise<ITypeOfView> => {
  const modifyURL = `${AGENT_URL}${APIM_AGENT}/ad/material/handle-type-of-view`;
  return apiClient
    .post<ITypeOfView>(modifyURL, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      return response.data;
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const getCoverpageByIdRequest = async (id: string, dispatch?: Dispatch<any>): Promise<ICoverpageDetail[]> => {
  const modifyURL = `${AGENT_URL}${APIM_AGENT}/ad/resource?resourceIds=${id}`;
  return apiClient
    .get<ICoverpageDetail[]>(modifyURL, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      return response.data;
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};

const convertToBgImageOption = (data: BgImageItem[], position: number) => {
  return data.map((item: BgImageItem, index: number) => {
    return {
      index: position + index + 1,
      id: item._id,
      value: item._id,
      label: item.name,
      imgUrl: item.image.url,
      textColorList: item.textColorList
        ? item.textColorList.map((color: string) => ({
            value: color,
            label: color,
          }))
        : defaultTextColorOptions,
    };
  });
};

export const fetchAllPromotionImageRequest = async ({
  name = '',
  page = 1,
  limit = 20,
}: IBgImageRequestParams): Promise<BgImagePaginateList> => {
  const query = {
    name,
    page,
    limit,
  };

  const queryPath = `${AGENT_URL}${APIM_AGENT}/ad/promotion-images?${qs.stringify(query)}`;

  return apiClient
    .get(queryPath, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response: any) => {
      if (response?.data?.items?.length > 0) {
        const position = (page - 1) * limit;
        response.data.items.forEach((item: any, index: number, arr: any[]) => {
          arr[index] = {
            index: position + index + 1,
            id: item._id,
            value: item._id,
            label: item.name,
            imgUrl: item.image.url,
          };
        });
      }

      return response.data ? { ...response.data, page, limit } : {};
    });
};

export const fetchCategoryList = async ({
  module,
  page = 1,
  pageSize = 1000,
}: {
  module: string;
  page?: number;
  pageSize?: number;
  [key: string]: any;
}): Promise<CategoryPaginateList> => {
  const query = {
    page,
    pageSize,
    filters: {
      isAvailableOnAgent: { $eq: true },
      module: { $eq: module },
    },
    sort: {
      name: 'ASC',
    },
  };

  const queryPath = `${AGENT_URL}${APIM_AGENT}/content-manager/collection-types/api::category.category?${qs.stringify(
    query,
  )}`;

  return apiClient
    .get(queryPath, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response) => {
      return {
        categoryList: response.data.results,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.pageCount,
        limit: response.data.pagination.pageSize,
        items: response.data.results,
        totalRecords: response.data.pagination.total,
        docs: response.data.results,
        hasNextPage: true,
        hasPrevPage: false,
        totalDocs: response.data.pagination.total,
      };
    });
};

function transformChildData(data: any) {
  const item = data.attributes;
  item.id = data.id;
  if (item.children) {
    item.children = item.children.data;
    item.children = item.children
      .filter((child: any) => child?.attributes?.isAvailableOnAgent)
      .map((data: any) => {
        const item = transformChildData(data);
        if (item.parent) {
          item.parent = item.parent?.data?.id;
        }
        return item;
      });
  }

  return item;
}

export const fetchCategoryItem = (id: string, dispatch?: Dispatch<any>): Promise<CategoryItem> => {
  const queryPath = `${AGENT_URL}${APIM_AGENT}/content-manager/api/categories/${id}?${qs.stringify({
    populate: {
      children: {
        populate: '*',
      },
      parent: {
        populate: '*',
      },
    },
  })}`;

  return apiClient
    .get(queryPath, {
      headers: {
        'raw-path': true,
      },
    })
    .then((response) => {
      const item = transformChildData(response.data.data);
      if (item.parent?.data) {
        item.parent.data.attributes.id = item.parent.data.id;
        item.parent = item.parent.data.attributes;
      }

      return item;
    })
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};
